import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { House, Sparkle, Image as ImageIcon, Baby, ArrowClockwise, Warning } from "@phosphor-icons/react";
import api from "@/lib/api";
import useAiJob from "@/hooks/useAiJob";

const PAGE_SIZE = 60;

/**
 * FamilyRecipesPanel — Family / kids / baby AI batch tool. Multi-select
 * country checkboxes (only 4 countries), paginated recipe list, and a
 * duplicate-detection banner shared with World Recipes.
 */
export default function FamilyRecipesPanel() {
  const [coverage, setCoverage] = useState(null);
  const [coverageLoading, setCoverageLoading] = useState(true);

  const [recipes, setRecipes] = useState([]);
  const [recipesTotal, setRecipesTotal] = useState(0);
  const [recipesLoading, setRecipesLoading] = useState(false);
  const [recipesHasMore, setRecipesHasMore] = useState(false);

  const [dupGroups, setDupGroups] = useState([]);
  const [dupLoading, setDupLoading] = useState(false);
  const [showDupes, setShowDupes] = useState(false);

  const [count, setCount] = useState(50);
  const [focusCountries, setFocusCountries] = useState([]);
  const [focusCategory, setFocusCategory] = useState("");
  const [focusAudience, setFocusAudience] = useState("");
  const [focusDiet, setFocusDiet] = useState("");
  const [generateImages, setGenerateImages] = useState(true);

  const loadCoverage = useCallback(async () => {
    setCoverageLoading(true);
    try {
      const { data } = await api.get("/admin/family-recipes/coverage");
      setCoverage(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load coverage");
    } finally {
      setCoverageLoading(false);
    }
  }, []);

  const loadRecipes = useCallback(async ({ append = false } = {}) => {
    setRecipesLoading(true);
    try {
      const skip = append ? recipes.length : 0;
      const { data } = await api.get("/admin/family-recipes/recent", {
        params: { limit: PAGE_SIZE, skip },
      });
      setRecipesTotal(data.total || 0);
      setRecipesHasMore(!!data.has_more);
      setRecipes((prev) => (append ? [...prev, ...(data.items || [])] : data.items || []));
    } catch (e) {
      /* silent */
    } finally {
      setRecipesLoading(false);
    }
  }, [recipes.length]);

  const loadDuplicates = useCallback(async () => {
    setDupLoading(true);
    try {
      const { data } = await api.get("/admin/family-recipes/duplicates");
      setDupGroups(data.groups || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load duplicates");
    } finally {
      setDupLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCoverage();
    loadRecipes();
    loadDuplicates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dupByRecipeId = useMemo(() => {
    const map = {};
    dupGroups.forEach((g) => {
      g.recipes.forEach((r) => { map[r.recipe_id] = { count: g.count, signature: g.signature }; });
    });
    return map;
  }, [dupGroups]);

  const job = useAiJob({
    statusUrl: (id) => `/admin/family-recipes/generate/status/${id}`,
    onDone: (data) => {
      toast.success(`Done — inserted ${data?.inserted ?? 0}, skipped ${data?.skipped ?? 0} duplicates.`);
      loadCoverage();
      loadRecipes();
      loadDuplicates();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || e?.message || "Batch failed"),
    onCancelled: () => {
      toast("Batch cancelled");
      loadCoverage();
      loadRecipes();
      loadDuplicates();
    },
  });

  const toggleCountry = (c) => {
    setFocusCountries((prev) => (prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]));
  };

  const startBatch = async () => {
    try {
      await job.start(() =>
        api
          .post("/admin/family-recipes/generate", {
            count: Number(count) || 50,
            focus_countries: focusCountries.length ? focusCountries : undefined,
            focus_category: focusCategory || undefined,
            focus_audience: focusAudience || undefined,
            focus_diet: focusDiet || undefined,
            generate_images: generateImages,
          })
          .then((r) => r.data),
      );
    } catch (_) { /* toast handled */ }
  };

  const deleteRecipe = async (recipeId, title) => {
    if (!window.confirm(`Delete "${title}"?\n\nThis removes it from the cookbook permanently.`)) return;
    try {
      await api.delete(`/recipes/${recipeId}?permanent=1`);
      toast.success("Deleted");
      loadRecipes();
      loadDuplicates();
      loadCoverage();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const progress = job.progress;
  const running = progress && progress.status === "running";

  const percent = useMemo(() => {
    if (!progress || !progress.total) return 0;
    return Math.min(100, Math.round((progress.processed / progress.total) * 100));
  }, [progress]);

  const coveragePercent = coverage
    ? Math.round((coverage.covered_count / (coverage.total_slots || 1)) * 100)
    : 0;

  const countries = coverage?.countries || [];

  return (
    <div className="space-y-8" data-testid="family-recipes-panel">
      {/* Header */}
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-2xl bg-rf-amber/20 flex items-center justify-center flex-shrink-0">
          <House size={26} weight="duotone" className="text-rf-amber-dark" />
        </div>
        <div>
          <h2 className="font-display text-2xl md:text-3xl font-black text-rf-ink">Family Recipes</h2>
          <p className="text-sm text-rf-ink-soft mt-1 max-w-2xl">
            Draft family, kids and baby-friendly recipes across mains, sides, desserts, cakes, tarts, jellies,
            snacks, sandwiches, bakes and salads — rooted in British, Irish, American and Canadian home cooking.
            All land in the <b>Inbox</b> cookbook <code className="text-xs bg-rf-cream px-1.5 py-0.5 rounded">cb_2016975e4b1d</code>
            {" "}for review, then publish public with the <b>RecipeFiler</b> tag.
          </p>
        </div>
      </div>

      {/* Coverage matrix */}
      <div className="rounded-2xl border border-rf-ink/10 bg-white p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-lg font-black text-rf-ink">Coverage</h3>
          <button
            onClick={loadCoverage}
            disabled={coverageLoading}
            data-testid="family-coverage-refresh"
            className="text-xs font-bold text-rf-ink-soft hover:text-rf-ink flex items-center gap-1 disabled:opacity-50"
          >
            <ArrowClockwise size={14} /> Refresh
          </button>
        </div>
        {coverageLoading && !coverage ? (
          <p className="text-sm text-rf-ink-soft">Loading…</p>
        ) : coverage ? (
          <>
            <div className="flex items-end gap-3 mb-2">
              <span className="font-display text-4xl font-black text-rf-ink" data-testid="family-covered-count">
                {coverage.covered_count}
              </span>
              <span className="text-lg text-rf-ink-soft mb-1">/ {coverage.total_slots} slots</span>
              <span className="ml-auto text-sm text-rf-ink-soft">{coveragePercent}% · {coverage.recipes_generated} recipes drafted</span>
            </div>
            <div className="w-full h-2 rounded-full bg-rf-cream overflow-hidden mb-5">
              <div className="h-full bg-rf-amber transition-all" style={{ width: `${coveragePercent}%` }} />
            </div>

            <div className="grid grid-cols-3 gap-3 mb-6">
              {(coverage.audiences || []).map((a) => (
                <div
                  key={a}
                  className="rounded-xl bg-rf-cream/60 border border-rf-ink/10 p-3"
                  data-testid={`family-audience-${a}`}
                >
                  <p className="text-[11px] font-bold uppercase tracking-wide text-rf-ink-soft flex items-center gap-1">
                    {a === "baby" && <Baby size={12} weight="duotone" />}
                    {a}
                  </p>
                  <p className="font-display text-2xl font-black text-rf-ink mt-0.5">
                    {coverage.audience_counts?.[a] ?? 0}
                  </p>
                </div>
              ))}
            </div>

            {/* Diet coverage row — click a card to focus the next batch on that diet */}
            {(coverage.diets || []).length > 0 && (
              <div className="mb-6">
                <p className="text-[11px] font-bold uppercase tracking-wide text-rf-ink-soft mb-2">Dietary requirement coverage</p>
                <div className="flex flex-wrap gap-2">
                  {coverage.diets.map((d) => {
                    const n = coverage.diet_counts?.[d.slug] ?? 0;
                    const active = focusDiet === d.slug;
                    return (
                      <button
                        key={d.slug}
                        type="button"
                        onClick={() => setFocusDiet(active ? "" : d.slug)}
                        data-testid={`family-diet-card-${d.slug}`}
                        className={`text-xs font-bold px-3 py-1.5 rounded-full border transition-colors ${
                          active
                            ? "bg-rf-amber text-rf-ink border-rf-amber"
                            : n > 0
                            ? "bg-rf-amber/15 text-rf-ink border-rf-ink/10 hover:bg-rf-amber/30"
                            : "bg-rf-cream text-rf-ink-soft border-rf-ink/10 hover:bg-white hover:text-rf-ink"
                        }`}
                        title={n > 0 ? `${n} recipe${n === 1 ? "" : "s"}` : "None yet — click to focus next batch"}
                      >
                        {d.label} · <span className="tabular-nums">{n}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-rf-ink-soft border-b border-rf-ink/10">
                    <th className="text-left py-2 pr-3 font-bold uppercase tracking-wide">Category</th>
                    {countries.map((c) => (
                      <th key={c} className="text-center py-2 px-2 font-bold">{c.replace("United ", "U. ")}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(coverage.categories || []).map((cat) => (
                    <tr key={cat} className="border-b border-rf-ink/5">
                      <td className="py-1.5 pr-3 font-bold text-rf-ink capitalize">{cat}</td>
                      {countries.map((c) => {
                        const n = coverage.matrix?.[c]?.[cat] ?? 0;
                        return (
                          <td key={c} className="text-center py-1.5 px-2">
                            <span
                              className={`inline-flex items-center justify-center min-w-[28px] h-6 px-2 rounded-full text-[11px] font-bold ${
                                n === 0
                                  ? "bg-rf-cream text-rf-ink-soft/60"
                                  : n < 3
                                  ? "bg-rf-amber/20 text-rf-ink"
                                  : "bg-rf-amber text-rf-ink"
                              }`}
                              data-testid={`family-matrix-${cat}-${c.replace(/ /g, "-").toLowerCase()}`}
                            >
                              {n}
                            </span>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : null}
      </div>

      {/* Batch controls */}
      <div className="rounded-2xl border border-rf-ink/10 bg-white p-6">
        <div className="flex items-center gap-2 mb-4">
          <Sparkle size={20} weight="duotone" className="text-rf-amber-dark" />
          <h3 className="font-display text-lg font-black text-rf-ink">Generate a new batch</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <label className="block">
            <span className="text-xs font-bold text-rf-ink-soft uppercase tracking-wide">Batch size</span>
            <input
              type="number"
              min="1"
              max="100"
              value={count}
              onChange={(e) => setCount(e.target.value)}
              data-testid="family-batch-count"
              disabled={running}
              className="mt-1.5 w-full rounded-lg border border-rf-ink/15 px-3 py-2 text-rf-ink focus:outline-none focus:border-rf-amber disabled:opacity-50"
            />
            <span className="text-[11px] text-rf-ink-soft mt-1 block">1-100. Chunks of 5.</span>
          </label>

          <div className="block">
            <span className="text-xs font-bold text-rf-ink-soft uppercase tracking-wide">Countries (tick any)</span>
            <div
              className="mt-1.5 flex flex-wrap gap-2 rounded-lg border border-rf-ink/15 bg-white px-3 py-2"
              data-testid="family-batch-countries"
            >
              {countries.map((c) => {
                const active = focusCountries.includes(c);
                return (
                  <label
                    key={c}
                    data-testid={`family-country-${c.replace(/ /g, "-").toLowerCase()}`}
                    className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs cursor-pointer border ${active ? "bg-rf-amber/25 border-rf-amber text-rf-ink font-bold" : "bg-white border-rf-ink/15 text-rf-ink-soft hover:bg-rf-cream"}`}
                  >
                    <input
                      type="checkbox"
                      checked={active}
                      onChange={() => toggleCountry(c)}
                      disabled={running}
                      className="w-3 h-3 accent-rf-amber"
                    />
                    {c}
                  </label>
                );
              })}
            </div>
            <span className="text-[11px] text-rf-ink-soft mt-1 block">Empty = mix all four.</span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <label className="block">
            <span className="text-xs font-bold text-rf-ink-soft uppercase tracking-wide">Category</span>
            <select
              value={focusCategory}
              onChange={(e) => setFocusCategory(e.target.value)}
              data-testid="family-batch-category"
              disabled={running}
              className="mt-1.5 w-full rounded-lg border border-rf-ink/15 px-3 py-2 text-rf-ink focus:outline-none focus:border-rf-amber disabled:opacity-50 bg-white capitalize"
            >
              <option value="">Any (mix)</option>
              {(coverage?.categories || []).map((c) => (
                <option key={c} value={c} className="capitalize">{c}</option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="text-xs font-bold text-rf-ink-soft uppercase tracking-wide">Audience</span>
            <select
              value={focusAudience}
              onChange={(e) => setFocusAudience(e.target.value)}
              data-testid="family-batch-audience"
              disabled={running}
              className="mt-1.5 w-full rounded-lg border border-rf-ink/15 px-3 py-2 text-rf-ink focus:outline-none focus:border-rf-amber disabled:opacity-50 bg-white capitalize"
            >
              <option value="">Any (mix)</option>
              {(coverage?.audiences || []).map((a) => (
                <option key={a} value={a} className="capitalize">{a}</option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="text-xs font-bold text-rf-ink-soft uppercase tracking-wide">Dietary requirement</span>
            <select
              value={focusDiet}
              onChange={(e) => setFocusDiet(e.target.value)}
              data-testid="family-batch-diet"
              disabled={running}
              className="mt-1.5 w-full rounded-lg border border-rf-ink/15 px-3 py-2 text-rf-ink focus:outline-none focus:border-rf-amber disabled:opacity-50 bg-white"
            >
              <option value="">No restriction</option>
              {(coverage?.diets || []).map((d) => (
                <option key={d.slug} value={d.slug}>{d.label}</option>
              ))}
            </select>
            <span className="text-[11px] text-rf-ink-soft mt-1 block">Locks every recipe in the batch to this diet with strict safety rules.</span>
          </label>
        </div>

        <div className="flex items-center gap-3 mb-5 rounded-lg border border-rf-ink/15 px-3 py-2 bg-white max-w-md">
          <input
            type="checkbox"
            checked={generateImages}
            onChange={(e) => setGenerateImages(e.target.checked)}
            data-testid="family-batch-images"
            disabled={running}
            className="w-4 h-4 accent-rf-amber"
          />
          <span className="text-sm text-rf-ink flex items-center gap-1.5">
            <ImageIcon size={16} weight="duotone" className="text-rf-ink-soft" />
            Generate + activate AI hero photo per recipe
          </span>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={startBatch}
            disabled={running || job.busy}
            data-testid="family-batch-start"
            className="px-5 py-2.5 rounded-full bg-rf-amber text-rf-ink font-bold text-sm hover:bg-rf-amber-dark disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
          >
            <Sparkle size={16} weight="fill" />
            {running ? "Running…" : `Draft ${count} recipes`}
          </button>
          {running && (
            <button
              onClick={job.cancel}
              data-testid="family-batch-cancel"
              className="px-4 py-2.5 rounded-full bg-white text-rf-ink font-bold text-sm border border-rf-ink/15 hover:bg-rf-cream"
            >
              Cancel
            </button>
          )}
        </div>

        {progress && (
          <div className="mt-6 rounded-xl bg-rf-cream/60 border border-rf-ink/10 p-4" data-testid="family-batch-progress">
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="font-bold text-rf-ink">
                Status: <span className="uppercase">{progress.status}</span>
              </span>
              <span className="text-rf-ink-soft">
                Chunk {progress.processed || 0} / {progress.total || 0}
              </span>
            </div>
            <div className="w-full h-2 rounded-full bg-white overflow-hidden mb-3">
              <div className="h-full bg-rf-amber transition-all" style={{ width: `${percent}%` }} />
            </div>
            <div className="flex flex-wrap gap-4 text-xs text-rf-ink-soft">
              <span><b className="text-rf-ink">{progress.inserted || 0}</b> inserted</span>
              <span><b className="text-rf-ink">{progress.skipped || 0}</b> skipped</span>
              <span>Target: <b className="text-rf-ink">{progress.requested || count}</b></span>
              {progress.focus_countries && Array.isArray(progress.focus_countries) && (
                <span>Countries: <b className="text-rf-ink">{progress.focus_countries.join(", ")}</b></span>
              )}
              {progress.focus_category && <span>Category: <b className="text-rf-ink capitalize">{progress.focus_category}</b></span>}
              {progress.focus_audience && <span>Audience: <b className="text-rf-ink capitalize">{progress.focus_audience}</b></span>}
              {progress.focus_diet && <span>Diet: <b className="text-rf-ink">{(coverage?.diets || []).find((d) => d.slug === progress.focus_diet)?.label || progress.focus_diet}</b></span>}
            </div>
            {progress.error && (
              <p className="mt-3 text-xs text-red-600">Error: {progress.error}</p>
            )}
          </div>
        )}
      </div>

      {/* Duplicate warning */}
      {dupGroups.length > 0 && (
        <div className="rounded-2xl border-2 border-red-300 bg-red-50 p-5" data-testid="family-dup-banner">
          <div className="flex items-start gap-3">
            <Warning size={22} weight="duotone" className="text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-display font-black text-red-900">
                {dupGroups.length} duplicate {dupGroups.length === 1 ? "title" : "titles"} detected
              </p>
              <p className="text-sm text-red-800 mt-0.5">
                Recipes with matching normalised titles across World, Family and manual entries. Review below and delete the extras.
              </p>
              <button
                onClick={() => setShowDupes((v) => !v)}
                data-testid="family-dup-toggle"
                className="mt-3 px-3 py-1.5 rounded-full bg-red-600 text-white text-xs font-bold hover:bg-red-700"
              >
                {showDupes ? "Hide" : "Review duplicates"}
              </button>
            </div>
          </div>

          {showDupes && (
            <div className="mt-5 space-y-4">
              {dupGroups.map((g) => (
                <div key={g.signature} className="rounded-xl bg-white border border-red-200 p-4">
                  <p className="text-xs font-bold uppercase tracking-wide text-red-700 mb-1">
                    {g.count} entries · signature: {g.signature}
                  </p>
                  <p className="text-sm text-rf-ink mb-3">Titles: <span className="italic">{g.titles.join(" · ")}</span></p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {g.recipes.map((r) => (
                      <div key={r.recipe_id} className="flex items-center gap-2 rounded-lg bg-rf-cream/50 border border-rf-ink/10 p-2 text-xs">
                        {r.ai_image_url ? (
                          <img src={r.ai_image_url} alt="" className="w-10 h-10 object-cover rounded" loading="lazy" />
                        ) : (
                          <div className="w-10 h-10 rounded bg-white border border-rf-ink/10" />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-bold text-rf-ink truncate">{r.title}</p>
                          <p className="text-rf-ink-soft truncate">
                            {r.source} · {(r.family_country || r.world_country || "—")} · {r.created_at?.slice(0, 10)}
                          </p>
                        </div>
                        <a
                          href={`/recipe/${r.recipe_id}`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-rf-ink-soft hover:text-rf-ink underline text-[11px]"
                        >
                          view
                        </a>
                        <button
                          onClick={() => deleteRecipe(r.recipe_id, r.title)}
                          data-testid={`family-dup-delete-${r.recipe_id}`}
                          className="text-[11px] font-bold text-red-600 hover:text-red-800"
                        >
                          delete
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* All recipes (paginated) */}
      <div className="rounded-2xl border border-rf-ink/10 bg-white p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-lg font-black text-rf-ink">
            AI family recipes <span className="text-rf-ink-soft font-normal">({recipesTotal})</span>
          </h3>
          <button
            onClick={() => { loadRecipes(); loadDuplicates(); }}
            disabled={recipesLoading || dupLoading}
            data-testid="family-recipes-refresh"
            className="text-xs font-bold text-rf-ink-soft hover:text-rf-ink flex items-center gap-1 disabled:opacity-50"
          >
            <ArrowClockwise size={14} /> Refresh
          </button>
        </div>
        {recipes.length === 0 && !recipesLoading ? (
          <p className="text-sm text-rf-ink-soft">No AI-generated family recipes yet.</p>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="family-recipes-grid">
              {recipes.map((r) => {
                const dup = dupByRecipeId[r.recipe_id];
                return (
                  <a
                    key={r.recipe_id}
                    href={`/recipe/${r.recipe_id}`}
                    target="_blank"
                    rel="noreferrer"
                    className="block rounded-xl overflow-hidden border border-rf-ink/10 hover:border-rf-amber transition-colors bg-rf-cream relative"
                    data-testid={`family-recipe-item-${r.recipe_id}`}
                  >
                    {dup && (
                      <span
                        className="absolute top-1.5 right-1.5 z-10 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-red-600 text-white text-[9px] font-black uppercase tracking-wide shadow"
                        title={`Duplicate title also appears ${dup.count - 1} other time(s)`}
                      >
                        <Warning size={9} weight="fill" /> Dup
                      </span>
                    )}
                    <div className="aspect-square bg-rf-cream flex items-center justify-center relative">
                      {r.ai_image_url ? (
                        <img src={r.ai_image_url} alt={r.title} className="w-full h-full object-cover" loading="lazy" />
                      ) : r.ai_image_status === "generating" ? (
                        <div className="text-[10px] font-bold text-rf-ink-soft flex flex-col items-center gap-1">
                          <Sparkle size={20} className="text-rf-amber animate-pulse" />
                          Generating
                        </div>
                      ) : r.ai_image_status === "failed" ? (
                        <div className="text-[10px] font-bold text-red-500 px-2 text-center">Image failed</div>
                      ) : (
                        <ImageIcon size={22} className="text-rf-ink-soft/40" />
                      )}
                      {r.family_audience === "baby" && (
                        <span className="absolute top-1.5 left-1.5 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-pink-100 text-pink-800 text-[9px] font-black uppercase tracking-wide">
                          <Baby size={10} weight="fill" /> Baby
                        </span>
                      )}
                      {r.family_audience === "kids" && (
                        <span className="absolute top-1.5 left-1.5 inline-flex items-center px-1.5 py-0.5 rounded-full bg-yellow-100 text-yellow-800 text-[9px] font-black uppercase tracking-wide">
                          Kids
                        </span>
                      )}
                    </div>
                    <div className="p-2.5">
                      <p className="text-xs font-bold text-rf-ink line-clamp-2">{r.title}</p>
                      <p className="text-[10px] text-rf-ink-soft mt-0.5 capitalize">
                        {r.family_country || "—"}
                        {r.family_category && ` · ${r.family_category}`}
                        {r.difficulty && ` · ${r.difficulty}`}
                      </p>
                      {r.family_diet && (
                        <span
                          className="inline-block mt-1 text-[9px] font-black uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800"
                          data-testid={`family-recipe-diet-${r.recipe_id}`}
                        >
                          {(coverage?.diets || []).find((d) => d.slug === r.family_diet)?.label || r.family_diet}
                        </span>
                      )}
                    </div>
                  </a>
                );
              })}
            </div>
            {recipesHasMore && (
              <div className="mt-6 flex justify-center">
                <button
                  onClick={() => loadRecipes({ append: true })}
                  disabled={recipesLoading}
                  data-testid="family-recipes-load-more"
                  className="px-5 py-2.5 rounded-full bg-white text-rf-ink font-bold text-sm border border-rf-ink/15 hover:bg-rf-cream disabled:opacity-50"
                >
                  {recipesLoading ? "Loading…" : `Load more (${recipesTotal - recipes.length} left)`}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
